import {
  CargoV2ListRequest,
  CargoV2DetailActivity,
  CargoV2MinimumWeightSummaryApiRequest,
  CargoV2ListClientParentApiRequest
} from "@/data/payload/api/CargoV2ApiRequest";
import {
  CargoSummaryInProcessRequest,
  FailedUnreadRequest
} from "@/data/payload/api/CargoApiRequest";
import { FlagRtcSeamless } from "@/feature-flags/flags-rtc-seamless";

const V3_BASE = "/hydra/v3/cargo";
const V4_BASE = "/hydra/v4/cargo";

/** Status value for in-progress list (matches v1 retry_cargo). */
export const CARGO_V2_IN_PROGRESS_STATUS =
  "process-belli";

/** Status value for cancelled/failed list (same as v1 retry_cargo). */
export const CARGO_V2_CANCELLED_STATUS = "cancel,failed";

export class CargoV2Endpoint {
  get isRtcSeamlessBookingV4Enabled() {
    return FlagRtcSeamless.permission_rtc_seamless_booking_v4_enable.isEnabled();
  }

  get versionBase() {
    if (this.isRtcSeamlessBookingV4Enabled) return V4_BASE;
    return V3_BASE;
  }
 
  getList(params: CargoV2ListRequest): string {
    return `${V3_BASE}/list?${params.toQueryString()}`;
  }

  /** GET /hydra/v3/retry_cargo (v3 equivalent of v1 retry_cargo for in-progress list). */
  getListRetryCargo(params: CargoV2ListRequest): string {
    return `/hydra/v3/retry_cargo?${params.toQueryString()}`;
  }

  getDetail(id: string | number): string {
    return `${V3_BASE}/${id}`;
  }

  getDetailWithActivity(
    id: string | number,
    activity?: CargoV2DetailActivity
  ): string {
    const params = new URLSearchParams();
    params.set("activity", activity ?? "");
    return `${V3_BASE}/detail/${id}?${params.toString()}`;
  }

  create(): string {
    return `${this.versionBase}`;
  }

  edit(id: string | number): string {
    return `${V3_BASE}/${id}`;
  }

  rebooking(id: string | number): string {
    return `${V3_BASE}/${id}/rebooking`;
  }

  cancel(id: string | number): string {
    return `${V3_BASE}/${id}/cancel`;
  }

  downloadReceipt(id: string | number): string {
    return `${V3_BASE}/${id}/download-receipt`;
  }

  downloadReceiptSync(id: string | number): string {
    return `${V3_BASE}/${id}/print-awb`;
  }

  delete(id: string | number): string {
    return `/hydra/v3/retry_cargo/${id}`;
  }

  getSuccessSummary(params: CargoV2ListRequest): string {
    return `${V3_BASE}/success-summary?${params.toQueryString()}`;
  }

  getDisputeSummary(params: CargoV2ListRequest): string {
    return `${V3_BASE}/dispute-summary?${params.toQueryString()}`;
  }

  getMinimumVolumeSummary(
    params: CargoV2MinimumWeightSummaryApiRequest
  ): string {
    return `${V3_BASE}/minimum-volume-summary?${params.toQueryString()}`;
  }

  downloadDisputeSummary(): string {
    return `${V3_BASE}/dispute-download`;
  }

  downloadMinimumVolume(): string {
    return `${V3_BASE}/minimum-volume-download`;
  }

  downloadListCargoSuccess(): string {
    return `${V3_BASE}/download-success`;
  }

  downloadFailed(): string {
    return `/hydra/v3/retry_cargo/download-failed`;
  }

  /** GET /hydra/v3/retry_cargo/summary-in-process */
  getSummaryInProcess(params: CargoSummaryInProcessRequest): string {
    return `/hydra/v3/retry_cargo/summary-in-process?${params.toQueryString()}`;
  }

  /** GET /hydra/v3/cargo/failed-unread */
  getFailedUnread(params: FailedUnreadRequest): string {
    return `${V3_BASE}/failed-unread?${params.toQueryString()}`;
  }

  /** GET /hydra/v3/ready-to-cargo */
  getReadyToCargoList(): string {
    return `/hydra/v3/ready-to-cargo`;
  }

  getDetailReadyToCargo(id: number): string {
    return `/hydra/v3/ready-to-cargo/detail/${id}`;
  }

  getLastIdRetryCargo(params: CargoV2ListRequest) {
    return `/hydra/v3/retry_cargo/last-id?${params.toQueryString()}`;
  }
  getLastIdCargo(params: CargoV2ListRequest) {
    return `${V3_BASE}/last-id?${params.toQueryString()}`;
  }

  downloadManifest(params: string, version = "v3"): string {
    return `/hydra/${version}/${params}`;
  }
  /** POST /hydra/v3/cargo/search-flight */
  searchFlight(): string {
    return `${this.versionBase}/search-flight`;
  }

  getRTCListClientParent(params: CargoV2ListClientParentApiRequest) {
    return `hydra/v3/ready-to-cargo/list-client-parent?${params.toQueryString()}`;
  }

  getCargoProductList(status: string) {
    return `/horde/v1/cargo-product?status=${status}`;
  }

  getCargoHistory(id: string) {
    return `/hydra/v3/cargo/history/${id}`;
  }

  getAwbValidation() {
    return `${V3_BASE}/awb-validation`;
  }

  calculateVolume(): string {
    return "hydra/v3/cargo/calculate";
  }

  getDetailRetryCargo(rcNo: string): string {
    return `/hydra/v3/retry_cargo/detail/${encodeURIComponent(rcNo)}`;
  }

  scan() {
    return `${V3_BASE}/scan`;
  }

  checkEligibleRtc(): string {
    return `/hydra/v1/ready-to-cargo/check-eligible`;
  }
}
